import { ChevronRightIcon, ClipboardDocumentIcon } from '@heroicons/react/24/outline';
import * as AccordionPrimitive from '@radix-ui/react-accordion';
import { useTranslation } from 'react-i18next';
import type { Judgement, MatchingCandidate } from '@/features/ai-matching-results/lib/types';
import { cn } from '@/shared/lib/utils';
import { Button, Select, Text, toast } from '@/shared/ui';
import type { SelectOption } from '@/shared/ui/form/select/helpers/types';
import { Markdown } from '@/shared/ui/markdown/Markdown';
import { JudgementDotLabel } from './JudgementDotLabel';

type Props = {
  candidate: MatchingCandidate;
  judgement: Judgement;
  onJudgementChange: (next: Judgement) => void;
  /** 現在開いている財源の候補をオレンジでハイライトする */
  highlighted: boolean;
};

/**
 * 補足（record_description）markdown 用のコンパクトスタイル。
 *
 * record_description は BE 生成の markdown で、それ自体に見出し（事業の概要 / 他自治体事例 等）を含む。
 * 既定の見出しサイズだと狭いサイドパネル内で本文・該当理由より目立ち、視覚階層が崩れるため、
 * 見出しを text-sm の太字程度に抑え、段落・リストの余白も詰める。
 */
const SUPPLEMENT_MARKDOWN_CLASS = cn(
  '[&_:first-child]:mt-0',
  '[&_h1]:mt-3 [&_h1]:mb-1 [&_h1]:font-bold [&_h1]:text-sm [&_h1]:text-text-base',
  '[&_h2]:mt-3 [&_h2]:mb-1 [&_h2]:font-bold [&_h2]:text-sm [&_h2]:text-text-base',
  '[&_h3]:mt-2 [&_h3]:mb-1 [&_h3]:font-semibold [&_h3]:text-sm [&_h3]:text-text-base',
  '[&_li]:my-0.5 [&_ol]:my-1 [&_ol]:list-decimal [&_ol]:pl-5 [&_p]:my-1 [&_ul]:my-1 [&_ul]:list-disc [&_ul]:pl-5',
);

/**
 * マッチングスキルカード内の候補 1 行（アコーディオン形式）
 *
 * 行は `>` 展開トリガー + 候補タイトル + 判定 Select で構成される。
 * `>` クリックで accordion 展開し、「該当理由」と「補足」（record_description / markdown）を表示する。
 * `highlighted=true` のとき、Figma 準拠でオレンジ系（warning）色 + 太字に切り替わる。
 *
 * Radix Accordion を直接利用しているのは、Select を Trigger ボタンの「外側」に置きたいため
 * （ネイティブの `<button>` 内に別の `<button>`（Select trigger）を入れると HTML ネスト違反になる）。
 */
export const MatchingCandidateRow = ({
  candidate,
  judgement,
  onJudgementChange,
  highlighted,
}: Props) => {
  const { t } = useTranslation();

  const judgementLabels: Record<Judgement, string> = {
    pending: t('ai-matching-results.judgement.pending'),
    matched: t('ai-matching-results.judgement.matched'),
    'not-matched': t('ai-matching-results.judgement.not-matched'),
  };

  const judgementOptions: SelectOption[] = (
    ['pending', 'matched', 'not-matched'] satisfies Judgement[]
  ).map((value) => ({
    value,
    label: <JudgementDotLabel judgement={value} label={judgementLabels[value]} />,
    searchLabel: judgementLabels[value],
  }));

  const hasDetails = (candidate.matchReasons?.length ?? 0) > 0 || Boolean(candidate.supplement);

  const displayTitle = candidate.title;

  const judgementSelectId = `judgement-select-${candidate.candidateId}`;

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(displayTitle);
      toast.success(t('ai-matching-results.matching-skill.copy-title-success'));
    } catch {
      // 非HTTPS・権限拒否等で writeText が reject した場合は失敗トーストを出す
      toast.error(t('ai-matching-results.matching-skill.copy-title-failed'));
    }
  };

  return (
    <AccordionPrimitive.Item
      value={candidate.candidateId}
      className={cn(
        'py-2.5',
        // 現在フォーカスしている（開いている）財源は背景色（淡い黄）でハイライトする
        highlighted && '-mx-2 rounded-md bg-surface-warning px-2',
      )}
    >
      <div className="group flex items-center gap-2">
        <AccordionPrimitive.Trigger
          disabled={!hasDetails}
          className={cn(
            'flex min-w-0 flex-1 cursor-pointer items-center gap-2 text-left',
            'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary',
            '[&[data-state=open]>svg]:rotate-90',
            !hasDetails && 'cursor-default',
          )}
        >
          <ChevronRightIcon
            className={cn(
              'size-4 shrink-0 transition-transform duration-200',
              highlighted ? 'text-warning' : 'text-text-secondary',
            )}
            aria-hidden
          />
          {/* ドロワーは最小320pxまで縮められ、固定120pxの判定セレクトにタイトル列が圧迫されるため、
              無制限折り返しだと行数が伸びすぎる。2行で省略し、超過分はホバーのツールチップで全文表示する。 */}
          <Text
            variant="body"
            truncate={2}
            className={cn('min-w-0', highlighted && 'font-bold text-warning')}
          >
            {displayTitle}
          </Text>
          {/* AccordionTrigger（button）内に置くため、Button は asChild で div[role="button"] として描画し
              button のネスト違反を避ける（component-conventions ルール）。Accordion 開閉と競合しないよう
              onClick / onKeyDown で stopPropagation する。 */}
          <Button
            asChild
            variant="ghost"
            size="icon"
            className="shrink-0 opacity-0 transition-opacity focus-visible:opacity-100 group-hover:opacity-100"
            aria-label={t('ai-matching-results.matching-skill.copy-title')}
            tooltip={{ content: t('ai-matching-results.matching-skill.copy-title') }}
          >
            {/* biome-ignore lint/a11y/useSemanticElements: AccordionTrigger(button) 内のためネイティブ button を使えず、意図的に div + role="button" */}
            <div
              role="button"
              tabIndex={0}
              onClick={(e) => {
                e.stopPropagation();
                handleCopy();
              }}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  e.stopPropagation();
                  handleCopy();
                }
              }}
            >
              <ClipboardDocumentIcon className="size-4 text-text-muted" />
            </div>
          </Button>
        </AccordionPrimitive.Trigger>
        {/* 判定セレクトは可視ラベルを持たないため、用途を sr-only ラベルで補い、
            スクリーンリーダーで「該当判定」と読めるようにする（htmlFor は Select の id と紐付く） */}
        <Text variant="label" htmlFor={judgementSelectId} className="sr-only">
          {t('ai-matching-results.matching-skill.judgement-select-label')}
        </Text>
        <Select
          id={judgementSelectId}
          value={judgement}
          onValueChange={(next) => onJudgementChange(next as Judgement)}
          options={judgementOptions}
          size="sm"
          rootClassName="w-[120px] shrink-0"
          triggerClassName="min-w-0"
        />
      </div>
      {hasDetails && (
        <AccordionPrimitive.Content className="overflow-hidden text-sm data-[state=closed]:animate-accordion-up data-[state=open]:animate-accordion-down">
          <div className="flex flex-col gap-2 pt-2 pl-6">
            {(candidate.matchReasons?.length ?? 0) > 0 && (
              <div className="flex flex-col gap-1">
                <Text className="font-bold">
                  {t('ai-matching-results.matching-skill.match-reasons-label')}
                </Text>
                <ul className="list-disc space-y-1 pl-5">
                  {candidate.matchReasons?.map((reason, index) => (
                    <li
                      // biome-ignore lint/suspicious/noArrayIndexKey: 該当理由は順序固定の表示専用なので index で十分
                      key={index}
                      className="rounded px-1"
                    >
                      <Text variant="body">{reason}</Text>
                    </li>
                  ))}
                </ul>
              </div>
            )}
            {/*
              補足（record_description）は「補足」見出しを付け、該当理由と同じセクション構造で表示する。
              本文は markdown（内部見出しは SUPPLEMENT_MARKDOWN_CLASS でコンパクトに抑制）。
            */}
            {candidate.supplement && (
              <div className="flex flex-col gap-1">
                <Text className="font-bold">
                  {t('ai-matching-results.matching-skill.supplement-label')}
                </Text>
                <Markdown useProse={false} className={SUPPLEMENT_MARKDOWN_CLASS}>
                  {candidate.supplement}
                </Markdown>
              </div>
            )}
          </div>
        </AccordionPrimitive.Content>
      )}
    </AccordionPrimitive.Item>
  );
};
